import { useCallback, useEffect, useState } from "react";

import { festivals, type LineupSource } from "./api";

/**
 * Where a community page's lineup comes from, and the day it was last checked.
 *
 * Shown in the app on the page itself (CONTENU.md §1): a page made without the
 * organiser says where it took its data and how fresh it is. "Checked today" is
 * one click, because it is the gesture repeated before each festival.
 */

/** Today in the browser's calendar, as YYYY-MM-DD. */
function today(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function SourceEditor({ slug }: { slug: string }) {
  const [state, setState] = useState<LineupSource | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setState(await festivals.source(slug).catch(() => null));
  }, [slug]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!state || !state.community) return null;

  const save = async (next: LineupSource) => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      await festivals.saveSource(slug, {
        label: next.label?.trim() || null,
        url: next.url?.trim() || null,
        checkedOn: next.checkedOn || null,
      });
      setMessage("Enregistré : la page l'affiche dès maintenant.");
      await reload();
    } catch {
      setError(
        "Enregistrement impossible. L'adresse doit commencer par http:// ou https://.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>Source du programme</h2>
      <p className="muted">
        Page communautaire : l'application affiche d'où vient le programme et le jour de
        sa dernière vérification. Revérifiez-le à l'approche du festival, quand les
        horaires bougent le plus.
      </p>
      {error ? <p className="error">{error}</p> : null}
      {message ? <p className="badge">{message}</p> : null}

      <label>
        Source (affichée telle quelle)
        <input
          value={state.label ?? ""}
          maxLength={120}
          placeholder="Site du festival, page Programmation"
          onChange={(e) => setState({ ...state, label: e.target.value })}
        />
      </label>
      <label>
        Adresse de la source
        <input
          type="url"
          value={state.url ?? ""}
          maxLength={500}
          placeholder="https://"
          onChange={(e) => setState({ ...state, url: e.target.value })}
        />
      </label>
      <label>
        Vérifié le
        <input
          type="date"
          value={state.checkedOn ?? ""}
          onChange={(e) => setState({ ...state, checkedOn: e.target.value })}
        />
      </label>

      <div className="row">
        <button className="primary" disabled={busy} onClick={() => void save(state)}>
          Enregistrer
        </button>
        <button
          disabled={busy}
          onClick={() => void save({ ...state, checkedOn: today() })}
        >
          Vérifié aujourd'hui
        </button>
      </div>
    </section>
  );
}
